import { BrowserModule } from '@angular/platform-browser';
import { ApplicationRef, DoBootstrap, inject, InjectionToken, LOCALE_ID, NgModule, NgZone } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { AppComponent } from './app.component';
import { AppModuleShared } from './app.module.shared';
import localeEs from '@angular/common/locales/es';
import { HttpClientModule } from '@angular/common/http';
import { RouteReuseStrategy, Router } from '@angular/router';
import { AppRouteReuseStrategy } from './components/core/_infrastructure/classes/appRouteReuseStrategy';
import { AuthService } from './components/core/_infrastructure/classes/services/auth.service';
import { catchError, Observable, of, tap } from 'rxjs';
import { ArrobaClaveService } from './components/core/_infrastructure/classes/services/arroba-clave.service';
import { BroadcastService } from './components/core/_infrastructure/classes/services/broadcast.service';
import { ConfigurationService } from './components/core/_infrastructure/classes/services/configuration.service';
import { MILISEGUNDOS_EN_MINUTO, MILISEGUNDOS_ANTICIPACION_REFRESH } from './components/core/_infrastructure/types/common';
import { NotificationService } from './components/core/_infrastructure/classes/services/notification.service';

registerLocaleData(localeEs, 'es');

export const BROADCAST_SERVICE = new InjectionToken<BroadcastService>('broadcastService');

@NgModule({
    imports: [BrowserModule, AppModuleShared, HttpClientModule],
    providers: [
        { provide: 'BASE_URL', useFactory: getBaseUrlServiceFactory },
        { provide: LOCALE_ID, useValue: 'es-ES' },
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        {
            provide: BROADCAST_SERVICE,
            useFactory: () => new BroadcastService(inject(NgZone)),
        },
    ],
    declarations: [],
})
export class AppModule implements DoBootstrap {
    private timeoutRefreshToken = 0;
    private timeoutRefresh: any;

    constructor(
        private notificationService: NotificationService,
        private authService: AuthService,
        private configurationService: ConfigurationService,
        private router: Router,
        private arrobaClaveService: ArrobaClaveService,
    ) {}

    async ngDoBootstrap(appRef: ApplicationRef) {
        this.arrobaClaveService.doInvokeOptions().subscribe((res: any) => {
            if (res?.StatusCode === 200) {
                return this.refreshTokenAndBootstrap(appRef);
            }
            return false;
        });
    }

    private refreshTokenAndBootstrap(appRef: ApplicationRef): void {
        this.authService.login$.subscribe(() => {
            this.autoRefreshToken();
        });

        this.authService
            .refreshToken()
            .pipe(
                tap((res: any) => {
                    if (!res && this.timeoutRefresh) {
                        clearInterval(this.timeoutRefresh);
                    }
                    appRef.bootstrap(AppComponent);
                }),
                catchError((err: any) => {
                    if (err?.error?.error) {
                        this.notificationService.addErrorMessage(err?.error?.error);
                    }
                    clearInterval(this.timeoutRefresh);
                    console.error('You are not allowed to see this page');
                    this.router.navigate(['/']);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private autoRefreshToken(): void {
        if (!this.timeoutRefreshToken) {
            this.configurationService.getValue('Auth:Jwt:TokenExpirationInMinutes').subscribe(result => {
                if (this.timeoutRefreshToken) {
                    return;
                }
                this.timeoutRefreshToken = JSON.parse(result); // Caducidad del token en minutos
                this.timeoutRefresh = setInterval(
                    () => {
                        this.authService
                            .refreshToken()
                            .pipe(
                                catchError((err: any) => {
                                    if (err?.error?.error) {
                                        this.notificationService.addErrorMessage(err?.error?.error);
                                    }
                                    clearInterval(this.timeoutRefresh);
                                    return of(false);
                                }),
                            )
                            .subscribe();
                    },
                    this.timeoutRefreshToken * MILISEGUNDOS_EN_MINUTO - MILISEGUNDOS_ANTICIPACION_REFRESH,
                    // Lanzamos el refresh "x" segundos antes de que caduque
                );
            });
        }
    }
}

export function getBaseUrlServiceFactory() {
    return document.getElementsByTagName('base')[0].href;
}

export type BaseUrlApi = ReturnType<typeof getBaseUrlServiceFactory>;

export const BASE_URL_SERVICE = new InjectionToken<BaseUrlApi>('Base Url Service', { factory: () => getBaseUrlServiceFactory() });
